/**
 * NextAuth.jsベースの認証フック
 * 既存のuseAuthインターフェースを維持しながらNextAuth.jsを使用
 */

'use client';

import { useSession, signIn, signOut } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import type { AuthenticatedUser } from '@/lib/auth/types';

export interface UseAuthReturn {
  user: AuthenticatedUser | null;
  loading: boolean;
  error: Error | null;
  signIn: (provider?: string) => Promise<void>;
  signOut: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
  updateProfile: (updates: Partial<AuthenticatedUser>) => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const { data: session, status, update } = useSession();
  const router = useRouter();
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);

  // セッションからAuthenticatedUser形式に変換
  // session.user.permissions は session-builder.ts が DB から計算した実際の権限
  const sessionPermissions = session?.user?.permissions;

  // 組織ロールを permissions から導出（session に直接含まれないため）
  const derivedOrgRole: 'admin' | 'member' = sessionPermissions?.canManageOrganization
    ? 'admin'
    : 'member';

  const user: AuthenticatedUser | null = session?.user ? {
    status: 'established' as const,
    id: session.user.id,
    cognitoId: session.user.cognitoId,
    email: session.user.email,
    name: session.user.name,
    avatarUrl: undefined, // TODO: アバター実装
    currentOrganizationId: session.user.currentOrganizationId || '',
    currentWorkspaceId: session.user.currentWorkspaceId,
    isInternal: session.user.isInternal === true,
    createdAt: new Date().toISOString(), // TODO: 実際の作成日時
    organizationMembership: {
      id: '',
      userId: session.user.id,
      organizationId: session.user.currentOrganizationId || '',
      role: derivedOrgRole,
      joinedAt: new Date().toISOString(),
      invitedBy: undefined,
      status: 'active' as const
    },
    organization: session.organization || {
      id: '',
      name: 'Default Organization',
      slug: 'default',
      settings: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    workspace: session.workspace ? {
      ...session.workspace,
      membership: session.user.workspaceMembership ? {
        role: session.user.workspaceMembership.role,
        joinedAt: session.user.workspaceMembership.joinedAt,
      } : undefined,
    } : undefined,
    permissions: sessionPermissions || {
      canManageOrganization: false,
      canInviteMembers: false,
      canManageBilling: false,
      canManageOrganizationKnowledge: false,
      canApproveOrganizationKnowledge: false,
      canCreateWorkspaces: false,
      canManageWorkspace: false,
      canCreatePages: false,
      canEditPages: false,
      canDeletePages: false,
      canSharePages: false,
      canCreateFlows: false,
      canEditFlows: false,
      canDeleteFlows: false,
      canShareFlows: false,
      canStartRecording: false,
      canComment: false
    }
  } : null;

  // オンボーディングが必要かチェック
  useEffect(() => {
    if (status === 'authenticated' && session?.user?.needsOnboarding) {
      router.push('/onboarding/welcome');
    }
  }, [status, session, router]);

  // サインイン
  const handleSignIn = useCallback(async (provider: string = 'cognito') => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await signIn(provider);
      if (result?.error) {
        throw new Error(result.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('サインインに失敗しました'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // サインアウト
  const handleSignOut = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // NextAuthでセッション削除後、/auth/signout にリダイレクト
      // 注意: NextAuthは同一オリジンのURLのみ許可するため、
      // Cognitoログアウト URL（外部URL）は callbackUrl に指定できない
      // /auth/signout ページで2段階目のCognitoログアウトリダイレクトを実行
      await signOut({
        callbackUrl: '/auth/signout',
        redirect: true
      });

      // この後のコードは実行されない（リダイレクトが発生するため）
    } catch (err) {
      // エラー時のみここが実行される
      const error = err instanceof Error ? err : new Error('サインアウトに失敗しました');
      setError(error);
      setLoading(false);

      // エラー時は常にサインインページへリダイレクト（エラーは投げない）
      window.location.href = '/auth/signin';
    }
    // finally節は不要（redirect: trueでリダイレクトするため到達不可）
  }, []);

  // 組織切り替え
  const switchOrganization = useCallback(async (organizationId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/auth/switch-organization', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ organizationId }),
      });

      if (!response.ok) {
        throw new Error('組織の切り替えに失敗しました');
      }

      // セッションを更新
      await update();
      
      // ダッシュボードにリダイレクト
      router.push('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err : new Error('組織の切り替えに失敗しました'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [update, router]);

  // ワークスペース切り替え
  // プロフィール更新
  const updateProfile = useCallback(async (updates: Partial<AuthenticatedUser>) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/auth/update-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error('プロフィールの更新に失敗しました');
      }

      // セッションを更新
      await update();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('プロフィールの更新に失敗しました'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [update]);

  return {
    user,
    loading: status === 'loading' || loading,
    error,
    signIn: handleSignIn,
    signOut: handleSignOut,
    switchOrganization,
    updateProfile,
  };
}
